/**
 * Shared wrapper for match-flow node player views.
 *
 * Provides the standard surface card with atmospheric background:
 * radial blur glow, noise overlay, and top/bottom gradient accent lines.
 * Variant `accent` switches the glow + border colors per node mood.
 */

import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type NodeCardAccent = "brand" | "red" | "amber";

const ACCENT_STYLES: Record<NodeCardAccent, { glow: string; topLine: string; bottomLine: string }> =
  {
    brand: {
      glow: "bg-brand-500/[0.04]",
      topLine: "bg-gradient-to-r from-transparent via-brand-500/30 to-transparent",
      bottomLine: "bg-gradient-to-r from-transparent via-brand-500/10 to-transparent",
    },
    red: {
      glow: "bg-red-500/[0.03]",
      topLine: "bg-gradient-to-r from-transparent via-red-500/20 to-transparent",
      bottomLine: "bg-gradient-to-r from-transparent via-red-500/10 to-transparent",
    },
    amber: {
      glow: "bg-amber-500/[0.03]",
      topLine: "bg-gradient-to-r from-transparent via-amber-500/30 to-transparent",
      bottomLine: "bg-gradient-to-r from-transparent via-amber-500/10 to-transparent",
    },
  };

type NodeCardProps = {
  children: ReactNode;
  accent?: NodeCardAccent;
};

export function NodeCard({ children, accent = "brand" }: NodeCardProps) {
  const styles = ACCENT_STYLES[accent];

  return (
    <div className="@container relative overflow-hidden rounded-xl bg-surface-900 border border-surface-700/50">
      {/* Atmospheric background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl",
            styles.glow,
          )}
        />
        <div className="absolute inset-0 noise-overlay" />
        <div className={cn("absolute top-0 left-0 right-0 h-px", styles.topLine)} />
        <div className={cn("absolute bottom-0 left-0 right-0 h-px", styles.bottomLine)} />
      </div>

      <div className="relative z-10 px-4 @md:px-8 @3xl:px-10 py-5 @md:py-6">{children}</div>
    </div>
  );
}
